import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environments';

/*
 * Pending resident SOS alerts announced by Firebase (data/* items with type 'SOS', status 'PENDING').
 * Lives here, not in the header, because the header is rebuilt on every navigation. The siren
 * stays docked in the header while GET /apartment/sos/pending-count is above 0 (or a new SOS
 * has just arrived), so opening SOS History doesn't hide it — handling the SOS does.
 */
@Injectable({ providedIn: 'root' })
export class SosAlertService {
  private readonly storageKey = `${environment.appName}sosAlerts`;
  // Every sosId already announced — the SOS item stays in Firebase, and each header rebuild
  // re-reads it, so this keeps an acknowledged SOS from sounding again (localStorage: all tabs)
  private readonly seenKey = `${environment.appName}sosSeen`;
  private seen: string[] = this.read(this.seenKey, localStorage);

  // sosIds still waiting for the admin
  pending$ = new BehaviorSubject<string[]>(this.read(this.storageKey, sessionStorage));
  // true while the full-screen siren is up; false = docked in the header
  expanded$ = new BehaviorSubject<boolean>(false);
  // A new SOS arrived — SOS History reloads its list
  arrived$ = new Subject<void>();
  // When the latest SOS arrived (shown on the full-screen alert)
  lastArrivedAt: Date | null = null;
  // Server's pending SOS count (header loads it; SOS History asks for a reload after a status change)
  pendingCount$ = new BehaviorSubject<number>(0);
  refreshCount$ = new Subject<void>();

  // What the docked siren shows: the server count, or new arrivals it hasn't counted yet
  get activeCount(): number {
    return Math.max(this.pendingCount$.value, this.pending.length);
  }

  get pending(): string[] {
    return this.pending$.value;
  }

  // Returns true when at least one SOS is new (so the full-screen siren should play)
  add(sosIds: string[]): boolean {
    const fresh = sosIds.filter((id) => !this.seen.includes(id));
    if (!fresh.length) {
      return false;
    }
    this.seen = [...this.seen, ...fresh].slice(-200);
    this.write(this.seenKey, this.seen, localStorage);
    this.save([...this.pending, ...fresh]);
    this.lastArrivedAt = new Date();
    this.expanded$.next(true);
    this.arrived$.next();
    return true;
  }

  // Handled elsewhere (status no longer PENDING)
  resolve(sosIds: string[]) {
    const left = this.pending.filter((id) => !sosIds.includes(id));
    if (left.length !== this.pending.length) {
      this.save(left);
    }
    if (!left.length) {
      this.expanded$.next(false);
    }
  }

  collapse() {
    this.expanded$.next(false);
  }

  // Admin opened SOS History: stop announcing (the dock stays while the server count is above 0)
  acknowledge() {
    this.save([]);
    this.expanded$.next(false);
  }

  private save(ids: string[]) {
    this.pending$.next(ids);
    this.write(this.storageKey, ids, sessionStorage);
  }

  private write(key: string, ids: string[], storage: Storage) {
    try {
      storage.setItem(key, JSON.stringify(ids));
    } catch { /* storage unavailable: alerts just won't survive a reload */ }
  }

  private read(key: string, storage: Storage): string[] {
    try {
      const ids = JSON.parse(storage.getItem(key) || '[]');
      return Array.isArray(ids) ? ids : [];
    } catch {
      return [];
    }
  }
}
